// src/components/MetaTags.tsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import type { Decision } from '../types/decision';

interface MetaTagsProps {
  decision?: Decision | null;
  pageType: 'landing' | 'decision' | 'chat' | 'share' | 'create' | 'terms' | 'privacy';
}

export function MetaTags({ decision, pageType }: MetaTagsProps) {
  // Base/default meta values
  let title = "WhatSayUs - AI-Powered Group Decision Making";
  let description = "Take the pain out of group decisions. Gather input from your team, community, or friends and summarize their views with AI.";
  let imageUrl = "https://www.whatsayus.com/social-preview.png";
  let url = window.location.href;
  let robotsContent = "index, follow";
  
  // Special og:title for decision-related pages that prioritizes the question
  let ogTitle = title;

  // Always noindex chat and share pages
  if (pageType === 'chat' || pageType === 'share') {
    robotsContent = "noindex, nofollow";
  }
  // For decision pages, check visibility
  else if (pageType === 'decision') {
    if (!decision || decision.visibility !== 'public') {
      robotsContent = "noindex, nofollow";
    }
  }
  
  // Override values for different page types
  if (pageType === 'decision' && decision) {
    title = `"${decision.question}" | WhatSayUs`;
    // For OG title, put the question first without quotes or site name
    ogTitle = decision.question;
    
    // Truncate long questions for meta titles
    if (title.length > 60) {
      title = `"${decision.question.substring(0, 57)}..." | WhatSayUs`;
      ogTitle = decision.question.substring(0, 80);
    }
    
    description = decision.details 
      ? `Group decision: ${decision.question}${decision.details.length > 0 ? ` - ${decision.details.substring(0, 80)}${decision.details.length > 80 ? '...' : ''}` : ''}`
      : `Group decision: ${decision.question}`;
    
    // Ensure description isn't too long
    if (description.length > 155) {
      description = description.substring(0, 152) + '...';
    }
    
    url = `https://www.whatsayus.com/d/${decision.id}`;
  }
  
  // Updated content for share pages
  if (pageType === 'share' && decision) {
    title = `Join the discussion: ${decision.question.substring(0, 50)}${decision.question.length > 50 ? '...' : ''} | WhatSayUs`;
    // For OG title, put the question first without branding
    ogTitle = decision.question;
    
    description = `Share your input on this group decision${decision.participants > 0 ? ` (${decision.participants} participants so far)` : ''}. Your view will be privately collected and included in the group summary.`;
    url = `https://www.whatsayus.com/d/${decision.id}/share`;
  }

  // Updated content for chat pages
  if (pageType === 'chat' && decision) {
    title = `Share your view: ${decision.question.substring(0, 50)}${decision.question.length > 50 ? '...' : ''} | WhatSayUs`;
    // For OG title, put the question first without branding
    ogTitle = decision.question;
    
    description = `Your perspective matters! Share your thoughts on this group decision through a private AI conversation.`;
    url = `https://www.whatsayus.com/d/${decision.id}/chat`;
  }
  
  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robotsContent} />
      
      {/* Open Graph / Facebook - with question-first approach */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="WhatSayUs" />
      <meta property="og:title" content={ogTitle} /> {/* Decision question prioritized */}
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={ogTitle} /> {/* Decision question prioritized */}
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* iOS specific tags */}
      <meta property="al:ios:url" content={url} />
      <meta property="al:ios:app_name" content="WhatSayUs" />
    </Helmet>
  );
}